/**
 * patient-portal i18n — FR default + AR (RTL). Registered once in the module's onRegister. Keys are
 * namespaced `pp.*`; missing keys fall back to FR then the raw key (never a crash, never a blank).
 * Clinical / status semantics always pair color + glyph + text — the labels here are the text half.
 */
import { registerMessages } from "@/app";

export function registerPatientPortalMessages(): void {
  registerMessages("fr", {
    // nav
    "nav.patient-portal.consentement": "Consentement patient",
    "nav.patient-portal.publication": "Publication Novera",
    "nav.patient-portal.resultats": "Aperçu compte-rendu",
    "nav.section.portail": "Portail patient (cloud)",

    // generic
    "pp.action.lookup": "Vérifier",
    "pp.action.refresh": "Actualiser",
    "pp.field.optional": "facultatif",
    "pp.cloud.badge": "Cloud uniquement",

    // not-available-on-this-install (the cloud-only degradation state)
    "pp.absent.title": "Module indisponible sur cette installation",
    "pp.absent.desc":
      "Le portail patient est un module cloud (installation centrale). Il n'est pas activé sur cette installation locale — aucune action n'est requise.",

    // consent-read screen
    "pp.consent.title": "Consentement de publication",
    "pp.consent.subtitle": "État vivant du consentement patient à la publication Novera (relevé en direct)",
    "pp.consent.field.patientId": "Identifiant patient",
    "pp.consent.field.patientId.hint": "Saisissez l'identifiant interne du patient pour relever son consentement.",
    "pp.consent.lookup": "Relever le consentement",
    "pp.consent.verdict.title": "Consentement publication Novera",
    "pp.consent.accorde": "Consentement accordé",
    "pp.consent.revoque": "Consentement absent / révoqué",
    "pp.consent.accorde.desc":
      "Le patient a accordé son consentement : ses résultats peuvent être publiés vers Novera.",
    "pp.consent.revoque.desc":
      "Aucun consentement actif : la publication vers Novera est bloquée (refus par défaut, médico-légal).",
    "pp.consent.live.note": "Vérification en direct — jamais une autorisation mise en cache.",
    "pp.consent.empty.title": "Aucun consentement relevé",
    "pp.consent.empty.desc": "Saisissez un identifiant patient pour relever l'état du consentement.",

    // publish-gate (seam-4) screen
    "pp.publish.title": "Porte de publication (seam 4)",
    "pp.publish.subtitle": "Contrôle préalable avant de publier un résultat vers Novera",
    "pp.publish.field.patientId": "Identifiant patient",
    "pp.publish.field.patientId.hint": "Le contrôle interroge canPublishToNovera en direct (refus par défaut).",
    "pp.publish.check": "Contrôler la porte",
    "pp.publish.gate.title": "Décision de publication",
    "pp.publish.allowed": "Publication autorisée",
    "pp.publish.blocked": "Publication bloquée",
    "pp.publish.allowed.desc":
      "Le consentement est actif — ce résultat peut être publié vers Novera (seam 4).",
    "pp.publish.blocked.desc":
      "Consentement absent ou révoqué — la publication vers Novera est refusée. Recueillez le consentement avant de republier.",
    "pp.publish.empty.title": "Aucun contrôle effectué",
    "pp.publish.empty.desc": "Saisissez un identifiant patient pour contrôler la porte de publication.",

    // result-view preview screen
    "pp.result.title": "Aperçu du compte-rendu",
    "pp.result.subtitle": "Aperçu fidèle (sans saisie) du compte-rendu émis présenté au patient",
    "pp.result.field.demandeId": "Identifiant de la demande",
    "pp.result.field.demandeId.hint": "Identifiant de la demande d'analyse pour afficher son compte-rendu émis.",
    "pp.result.lookup": "Afficher le compte-rendu",
    "pp.result.meta.numero": "Numéro",
    "pp.result.meta.version": "Version",
    "pp.result.pdf": "Ouvrir le PDF (bilingue)",
    "pp.result.pdf.absent": "PDF non disponible",
    "pp.result.col.analyse": "Analyse",
    "pp.result.col.valeur": "Valeur",
    "pp.result.col.unite": "Unité",
    "pp.result.col.reference": "Intervalle de référence",
    "pp.result.col.drapeau": "Interprétation",
    "pp.result.lignes.empty.title": "Compte-rendu sans ligne",
    "pp.result.lignes.empty.desc": "Ce compte-rendu émis ne contient aucune ligne de résultat.",
    "pp.result.none.title": "Aucun compte-rendu émis",
    "pp.result.none.desc":
      "Aucun compte-rendu n'a encore été émis pour cette demande. Seuls les comptes-rendus validés et émis sont visibles.",
    "pp.result.empty.title": "Aucun compte-rendu chargé",
    "pp.result.empty.desc": "Saisissez un identifiant de demande pour afficher l'aperçu du compte-rendu.",

    // clinical flags (text half of color+glyph+text)
    "pp.flag.normal": "Dans l'intervalle",
    "pp.flag.bas": "Bas",
    "pp.flag.haut": "Haut",
    "pp.flag.critique": "Valeur panique",
    "pp.flag.inconnu": "Non interprété",

    "pp.na": "—",
  });

  registerMessages("ar", {
    "nav.patient-portal.consentement": "موافقة المريض",
    "nav.patient-portal.publication": "النشر إلى Novera",
    "nav.patient-portal.resultats": "معاينة التقرير",
    "nav.section.portail": "بوابة المريض (سحابة)",

    "pp.action.lookup": "تحقّق",
    "pp.action.refresh": "تحديث",
    "pp.field.optional": "اختياري",
    "pp.cloud.badge": "سحابي فقط",

    "pp.absent.title": "الوحدة غير متوفّرة على هذا التثبيت",
    "pp.absent.desc":
      "بوابة المريض وحدة سحابية (تثبيت مركزي). غير مفعّلة على هذا التثبيت المحلي — لا يلزم أي إجراء.",

    "pp.consent.title": "موافقة النشر",
    "pp.consent.subtitle": "الحالة الحيّة لموافقة المريض على النشر إلى Novera (قراءة مباشرة)",
    "pp.consent.field.patientId": "معرّف المريض",
    "pp.consent.field.patientId.hint": "أدخل المعرّف الداخلي للمريض لقراءة موافقته.",
    "pp.consent.lookup": "قراءة الموافقة",
    "pp.consent.verdict.title": "موافقة النشر إلى Novera",
    "pp.consent.accorde": "موافقة ممنوحة",
    "pp.consent.revoque": "موافقة غائبة / ملغاة",
    "pp.consent.accorde.desc": "منح المريض موافقته: يمكن نشر نتائجه إلى Novera.",
    "pp.consent.revoque.desc": "لا توجد موافقة نشطة: النشر إلى Novera محظور (رفض افتراضي، طبي-قانوني).",
    "pp.consent.live.note": "تحقّق مباشر — وليس تفويضًا مخزّنًا.",
    "pp.consent.empty.title": "لم تُقرأ أي موافقة",
    "pp.consent.empty.desc": "أدخل معرّف المريض لقراءة حالة الموافقة.",

    "pp.publish.title": "بوابة النشر (seam 4)",
    "pp.publish.subtitle": "تحقّق مسبق قبل نشر نتيجة إلى Novera",
    "pp.publish.field.patientId": "معرّف المريض",
    "pp.publish.field.patientId.hint": "يستعلم التحقّق عن canPublishToNovera مباشرة (رفض افتراضي).",
    "pp.publish.check": "تحقّق من البوابة",
    "pp.publish.gate.title": "قرار النشر",
    "pp.publish.allowed": "النشر مسموح",
    "pp.publish.blocked": "النشر محظور",
    "pp.publish.allowed.desc": "الموافقة نشطة — يمكن نشر هذه النتيجة إلى Novera (seam 4).",
    "pp.publish.blocked.desc":
      "الموافقة غائبة أو ملغاة — يُرفض النشر إلى Novera. اجمع الموافقة قبل إعادة النشر.",
    "pp.publish.empty.title": "لم يُجرَ أي تحقّق",
    "pp.publish.empty.desc": "أدخل معرّف المريض للتحقّق من بوابة النشر.",

    "pp.result.title": "معاينة التقرير",
    "pp.result.subtitle": "معاينة مطابقة (للقراءة فقط) للتقرير الصادر المعروض على المريض",
    "pp.result.field.demandeId": "معرّف الطلب",
    "pp.result.field.demandeId.hint": "معرّف طلب التحليل لعرض تقريره الصادر.",
    "pp.result.lookup": "عرض التقرير",
    "pp.result.meta.numero": "الرقم",
    "pp.result.meta.version": "الإصدار",
    "pp.result.pdf": "فتح PDF (ثنائي اللغة)",
    "pp.result.pdf.absent": "PDF غير متوفّر",
    "pp.result.col.analyse": "التحليل",
    "pp.result.col.valeur": "القيمة",
    "pp.result.col.unite": "الوحدة",
    "pp.result.col.reference": "المجال المرجعي",
    "pp.result.col.drapeau": "التفسير",
    "pp.result.lignes.empty.title": "تقرير بلا أسطر",
    "pp.result.lignes.empty.desc": "لا يحتوي هذا التقرير الصادر على أي سطر نتيجة.",
    "pp.result.none.title": "لا يوجد تقرير صادر",
    "pp.result.none.desc":
      "لم يصدر بعد أي تقرير لهذا الطلب. تظهر فقط التقارير المعتمدة والصادرة.",
    "pp.result.empty.title": "لم يُحمّل أي تقرير",
    "pp.result.empty.desc": "أدخل معرّف طلب لعرض معاينة التقرير.",

    "pp.flag.normal": "ضمن المجال",
    "pp.flag.bas": "منخفض",
    "pp.flag.haut": "مرتفع",
    "pp.flag.critique": "قيمة حرجة",
    "pp.flag.inconnu": "غير مفسّر",

    "pp.na": "—",
  });
}
